/**
 * Render registry — opt-in chat-bubble component mounting.
 *
 * Default contract: tools return `{ display, data?, error? }` and Chat
 * renders `display` verbatim. That's plenty for almost every tool.
 *
 * Some tools (currently: tarot) need interactive UI inside the chat — a
 * spread picker, a card grid, a streamed synthesis paragraph. For those,
 * the tool returns:
 *
 *     display: "<plain-text fallback for chat history>"
 *     data: { render: "<componentKey>", props: { ... } }
 *
 * The chat client detects this `data.render` shape and mounts the
 * registered component in place of the chat bubble. The component is
 * given `props` plus an `onComplete(wrapUpText)` callback. When it
 * calls onComplete, the rendered bubble is replaced with the plain
 * `display` line (so the persisted chat history has a textual record)
 * and a new bilbo message is appended with the wrap-up text.
 *
 * Persistence note: `data.render` does NOT survive a page reload. The
 * persisted bubble is the fallback `display` text. Active in-chat
 * sessions are one-shot — if you reload mid-session, the session
 * continues server-side and can be resumed via the relevant tool, but
 * the in-bubble UI is gone.
 *
 * Adding a new render: import the component into this file and add it
 * to RENDER_REGISTRY. The key is what the tool's `data.render` returns.
 */

import type { ComponentType } from 'react';
import { TarotReading } from './tarot/TarotReading';
import { Workout } from './gym/Workout';

export interface RenderComponentProps<P = Record<string, unknown>> {
  props: P;
  onComplete: (wrapUpText: string) => void;
  /** Unmount the render bubble without adding a new bilbo message. Optionally
   *  replaces the bubble's text (e.g. "✓ Reading deleted."). */
  onDismiss: (content?: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const RENDER_REGISTRY: Record<string, ComponentType<RenderComponentProps<any>>> = {
  tarot: TarotReading,
  workout: Workout,
};

export function getRenderComponent(
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<RenderComponentProps<any>> | null {
  return RENDER_REGISTRY[key] ?? null;
}
